import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { User } from "firebase/auth";
import { auth } from "../firebase";
import { setUserId, setUserProperties } from "../utility/analytics.utils";
import { clearVerificationEmailRecord } from "../components/Auth/utils/authValidation";

interface AppContextValue {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  active: string;
  setActive: Dispatch<SetStateAction<string>>;
  reloadStories: boolean;
  triggerReloadStories: () => void;
  setReloadStories: Dispatch<SetStateAction<boolean>>;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

interface AppContextProviderProps {
  children: ReactNode;
}

export const AppContextProvider = ({
  children,
}: AppContextProviderProps): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [active, setActive] = useState("home");

  const [reloadStories, setReloadStories] = useState(false);
  const triggerReloadStories = () => setReloadStories((prev) => !prev);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      // Only set user in context if email is verified
      // Keep unverified users signed in (for resend functionality) but don't set in context
      if (authUser && !authUser.emailVerified) {
        // Don't set unverified user in context - ProtectedRoute will block them
        // But keep them signed in so resend verification can work
        setUser(null);
        setUserId(null);
        return;
      }
      
      setUser(authUser ?? null);
      
      // Set user properties for analytics
      if (authUser && authUser.emailVerified) {
        // Clear verification email record since email is now verified
        if (authUser.email) {
          clearVerificationEmailRecord(authUser.email);
        }
        
        setUserId(authUser.uid);
        setUserProperties({
          user_id: authUser.uid,
          email: authUser.email || "",
          display_name: authUser.displayName || "",
        });
      } else {
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        active,
        setActive,
        reloadStories,
        triggerReloadStories,
        setReloadStories,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextValue => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppContextProvider");
  }
  return context;
};
